import React, { useState, useEffect } from 'react';
import {
  Card, Typography, Button, Flex, theme, Select, Spin, Empty, Tag,
  Form, Input, Checkbox, Space, Divider, Row, Col, Alert, Tooltip, Modal, message
} from 'antd';
import {
  NodeIndexOutlined, CheckCircleOutlined, StopOutlined, ArrowLeftOutlined,
  PlusOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined, TagsOutlined
} from '@ant-design/icons';
import { ProcessFlow } from '../../../../types';
import { useWorkflows } from '../../../../hooks/useWorkflows';
import n8nLogo from '../../../../../../assets/img/n8n-logo.svg';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { useToken } = theme;

export interface ProcessFlowParameter {
  id: string;
  name: string;
  description: string;
  dataType: 'string' | 'number' | 'boolean' | 'array' | 'object';
  required: boolean;
  enumValues?: string[];
}

interface ProcessFlowSelectorProps {
  assistantId: string;
  onSave: (message: string) => void;
  onCancel: () => void;
  compact?: boolean;
  initialData?: {
    selectedWorkflowId?: string;
    parameters?: ProcessFlowParameter[];
  };
}

export const ProcessFlowSelector: React.FC<ProcessFlowSelectorProps> = ({
  assistantId,
  onSave,
  onCancel,
  compact = false,
  initialData
}) => {
  const { token } = useToken();
  const { workflows, loading, error } = useWorkflows(assistantId);
  const [selectedWorkflow, setSelectedWorkflow] = useState<ProcessFlow | null>(null);
  const [customName, setCustomName] = useState<string>('');
  const [customDescription, setCustomDescription] = useState<string>('');
  const [customUrl, setCustomUrl] = useState<string>('');
  const [parameters, setParameters] = useState<ProcessFlowParameter[]>(initialData?.parameters || []);
  const [showParameterForm, setShowParameterForm] = useState(false);
  const [editingParameter, setEditingParameter] = useState<ProcessFlowParameter | null>(null);
  const [form] = Form.useForm();

  // Initialize with existing data when editing
  useEffect(() => {
    if (initialData?.selectedWorkflowId && workflows.length > 0) {
      const workflow = workflows.find(w => w.id === initialData.selectedWorkflowId);
      if (workflow) {
        setSelectedWorkflow(workflow);
        setCustomName(workflow.name);
        setCustomDescription(workflow.description || '');
        setCustomUrl(workflow.n8nWebhookURL || workflow.workflowExcectionUrl || '');
      }
    }
  }, [initialData?.selectedWorkflowId, workflows]);

  const dataTypeOptions = [
    { label: 'Text', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' },
    { label: 'Array', value: 'array' },
    { label: 'Object', value: 'object' }
  ];

  const getDataTypeColor = (dataType: string) => {
    const colorMap: { [key: string]: string } = {
      string: 'blue', number: 'green', boolean: 'orange', array: 'purple', object: 'red'
    };
    return colorMap[dataType] || 'default';
  };

  const getStatusIcon = (status: string) => {
    return status === 'active' ?
      <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
      <StopOutlined style={{ color: '#f5222d' }} />;
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? 'green' : 'red';
  };

  const handleSelectChange = (workflowId: string | null) => {
    const workflow = workflows.find(w => w.id === workflowId);
    setSelectedWorkflow(workflow || null);

    // Set default values from selected workflow
    if (workflow) {
      setCustomName(workflow.name);
      setCustomDescription(workflow.description || '');
      setCustomUrl(workflow.n8nWebhookURL || workflow.workflowExcectionUrl || '');
    } else {
      setCustomName('');
      setCustomDescription('');
      setCustomUrl('');
    }
  };

  const handleAddParameter = () => {
    setEditingParameter(null);
    form.resetFields();
    setShowParameterForm(true);
  };

  const handleEditParameter = (parameter: ProcessFlowParameter) => {
    setEditingParameter(parameter);
    form.setFieldsValue({
      name: parameter.name,
      description: parameter.description,
      dataType: parameter.dataType,
      required: parameter.required,
      enumValues: parameter.enumValues?.join('\n') || ''
    });
    setShowParameterForm(true);
  };

  const handleSaveParameter = async () => {
    try {
      const values = await form.validateFields();
      const newParameter: ProcessFlowParameter = {
        id: editingParameter?.id || Date.now().toString(),
        name: values.name,
        description: values.description,
        dataType: values.dataType,
        required: values.required || false,
        enumValues: values.enumValues
          ? values.enumValues.split('\n').map((v: string) => v.trim()).filter((v: string) => v.length > 0)
          : undefined
      };

      if (editingParameter) {
        setParameters(prev => prev.map(p => p.id === editingParameter.id ? newParameter : p));
      } else {
        setParameters(prev => [...prev, newParameter]);
      }

      setShowParameterForm(false);
      form.resetFields();
      setEditingParameter(null);
    } catch (error) {
      logger.error('Validation failed:', error);
    }
  };

  const handleDeleteParameter = (parameterId: string) => {
    setParameters(prev => prev.filter(p => p.id !== parameterId));
  };

  // Convert string to snake_case
  const toSnakeCase = (str: string): string => {
    return str
      .trim()
      .replace(/\s+/g, '_')           // Replace spaces with underscores
      .replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)  // Convert camelCase
      .replace(/[-]+/g, '_')          // Replace dashes with underscores
      .replace(/[^\w]+/g, '_')        // Replace special chars with underscores
      .replace(/^_+|_+$/g, '')        // Remove leading/trailing underscores
      .replace(/_+/g, '_')            // Replace multiple underscores with single
      .toLowerCase();
  };

  const handleSave = () => {
    if (!selectedWorkflow) {
      message.error('Please select a process flow');
      return;
    }

    if (!customName.trim()) {
      message.error('Please enter a name for this process flow');
      return;
    }

    if (!customUrl.trim()) {
      message.error('Please enter a webhook URL for this process flow');
      return;
    }

    const functionName = toSnakeCase(customName);

    // Build properties object with required engine and url
    const properties: any = {
      engine: {
        description: "Motor de ejecución",
        type: "string",
        enum: [selectedWorkflow.engine || "n8n"]
      },
      url: {
        description: "URL del webhook para ejecutar la función",
        type: "string",
        enum: [customUrl.trim()]
      }
    };

    // Add custom parameters to properties
    const requiredFields = ["engine", "url"];
    parameters.forEach(param => {
      const paramProperty: any = {
        description: param.description,
        type: param.dataType
      };

      // Add enum values if they exist
      if (param.enumValues && param.enumValues.length > 0) {
        paramProperty.enum = param.enumValues;
      }

      properties[param.name] = paramProperty;

      // Add to required array if parameter is required
      if (param.required) {
        requiredFields.push(param.name);
      }
    });

    // Build OpenAI function calling format
    const processFlowConfig = {
      chatbotId: assistantId,
      type: "function",
      name: functionName,
      function: {
        name: functionName,
        description: customDescription.trim(),
        parameters: {
          type: "object",
          properties: properties,
          required: requiredFields
        }
      }
    };

    const messageString = `Execute Process Flow: ${JSON.stringify(processFlowConfig)}`;
    onSave(messageString);
  };

  if (loading) {
    return (
      <div style={{ padding: token.paddingLG, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: token.paddingLG, textAlign: 'center' }}>
        <Text type="danger">{error}</Text>
      </div>
    );
  }

  if (workflows.length === 0) {
    return (
      <div style={{ padding: token.paddingLG, textAlign: 'center' }}>
        <Empty description="No process flows available" />
      </div>
    );
  }

  return (
    <div style={{ padding: compact ? token.paddingMD : token.paddingLG }}>
      <Flex vertical gap="large" style={{ gap: '24px' }}>
        {/* Header */}
        <Flex align="center" gap="medium">
          <Button icon={<ArrowLeftOutlined />} type="text" onClick={onCancel} />
          <div>
            <Title level={4} style={{ margin: 0 }}>
              <NodeIndexOutlined style={{ marginRight: '8px', color: token.colorPrimary }} />
              Configure Process Flow
            </Title>
            <Text type="secondary">Select a workflow and configure its parameters</Text>
          </div>
        </Flex>

        {/* Process Flow Selection */}
        <Card>
          <Title level={5}>Select Process Flow</Title>
          <Select
            placeholder="Choose a process flow..."
            value={selectedWorkflow?.id || undefined}
            onChange={handleSelectChange}
            style={{ width: '100%' }}
            size="large"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
            }
            options={workflows.map(workflow => ({
              value: workflow.id,
              label: workflow.name,
              workflow
            }))}
            optionRender={(option) => (
              <Flex justify="space-between" align="center">
                <div>
                  <Text strong>{option.data.workflow.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {option.data.workflow.description}
                  </Text>
                </div>
                <Tag
                  color={getStatusColor(option.data.workflow.status)}
                  icon={getStatusIcon(option.data.workflow.status)}
                >
                  {option.data.workflow.status}
                </Tag>
              </Flex>
            )}
          />

          {selectedWorkflow && (
            <Card size="small" style={{ marginTop: token.marginMD, backgroundColor: token.colorFillTertiary }}>
              <Flex align="center" gap="medium">
                <img
                  src={n8nLogo}
                  alt="n8n"
                  style={{ width: '40px', height: '40px' }}
                />
                <div style={{ flex: 1 }}>
                  <Text strong>{selectedWorkflow.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {selectedWorkflow.description}
                  </Text>
                </div>
                <Tag
                  color={getStatusColor(selectedWorkflow.status)}
                  icon={getStatusIcon(selectedWorkflow.status)}
                >
                  {selectedWorkflow.status}
                </Tag>
              </Flex>
            </Card>
          )}
        </Card>

        {/* Custom Name and Description */}
        {selectedWorkflow && (
          <Card>
            <Title level={5}>
              Flow Details
            </Title>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: token.marginMD }}>
              Customize the name and description for this process flow in your conversation flow
            </Text>

            <Form layout="vertical">
              <Form.Item
                label="Display Name"
                required
                tooltip="This name will be used to reference this process flow in the conversation"
                extra={customName ? <Text type="secondary" style={{ fontSize: '11px' }}>Will be saved as: <Text code>{toSnakeCase(customName)}</Text></Text> : null}
              >
                <Input
                  placeholder="Enter a descriptive name..."
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  size="large"
                  maxLength={100}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="Description"
                tooltip="Describe what this process flow does in this specific conversation context"
              >
                <TextArea
                  placeholder="Enter a description..."
                  value={customDescription}
                  onChange={(e) => setCustomDescription(e.target.value)}
                  rows={3}
                  maxLength={500}
                  showCount
                />
              </Form.Item>

              <Form.Item
                label="Webhook URL"
                required
                tooltip="The webhook URL that will be called to execute this process flow"
              >
                <Input
                  placeholder="https://n8n.example.com/webhook/..."
                  value={customUrl}
                  onChange={(e) => setCustomUrl(e.target.value)}
                  size="large"
                  type="url"
                />
              </Form.Item>
            </Form>
          </Card>
        )}

        {/* Parameters Configuration */}
        {selectedWorkflow && (
          <Card>
            <Flex align="center" justify="space-between" style={{ marginBottom: token.marginMD }}>
              <Title level={5} style={{ margin: 0 }}>Parameters ({parameters.length})</Title>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddParameter}>
                Add Parameter
              </Button>
            </Flex>

            {parameters.length === 0 ? (
              <Alert
                message="No parameters configured"
                description="This process flow will run without input parameters. Add parameters to make it dynamic."
                type="info"
                showIcon
              />
            ) : (
              <Row gutter={[16, 16]}>
                {parameters.map((parameter) => (
                  <Col xs={24} sm={12} lg={8} key={parameter.id}>
                    <Card
                      size="small"
                      hoverable
                      bodyStyle={{ padding: '16px' }}
                      style={{
                        borderRadius: '12px',
                        border: `1px solid ${token.colorBorderSecondary}`,
                        transition: 'all 0.2s ease',
                        background: token.colorFillAlter
                      }}
                    >
                      <Flex vertical gap="12px">
                        {/* Header with name, type, required, and actions */}
                        <Flex justify="space-between" align="center">
                          <Flex align="center" gap="8px" wrap>
                            <Text strong style={{ fontSize: '14px', color: token.colorText }}>
                              {parameter.name}
                            </Text>
                            <Tag
                              color={getDataTypeColor(parameter.dataType)}
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '6px',
                                margin: 0,
                                fontWeight: '500'
                              }}
                            >
                              {parameter.dataType}
                            </Tag>
                            {parameter.required && (
                              <Tag
                                color="red"
                                style={{
                                  fontSize: '9px',
                                  padding: '1px 4px',
                                  borderRadius: '4px',
                                  margin: 0,
                                  fontWeight: '500'
                                }}
                              >
                                REQUIRED
                              </Tag>
                            )}
                          </Flex>
                          <Space size="small">
                            <Button
                              icon={<EditOutlined />}
                              type="text"
                              size="small"
                              onClick={() => handleEditParameter(parameter)}
                              style={{
                                color: token.colorTextSecondary,
                                fontSize: '12px',
                                width: '24px',
                                height: '24px'
                              }}
                            />
                            <Button
                              icon={<DeleteOutlined />}
                              type="text"
                              size="small"
                              danger
                              onClick={() => handleDeleteParameter(parameter.id)}
                              style={{
                                fontSize: '12px',
                                width: '24px',
                                height: '24px'
                              }}
                            />
                          </Space>
                        </Flex>

                        {/* Description */}
                        <Text
                          type="secondary"
                          style={{
                            fontSize: '12px',
                            lineHeight: 1.4,
                            color: token.colorTextSecondary
                          }}
                        >
                          {parameter.description}
                        </Text>

                        {/* Enum values with beautiful design */}
                        {parameter.enumValues && parameter.enumValues.length > 0 && (
                          <div style={{
                            backgroundColor: token.colorFillSecondary,
                            borderRadius: '8px',
                            padding: '8px',
                            border: `1px solid ${token.colorBorder}`
                          }}>
                            <Flex align="center" gap="6px" style={{ marginBottom: '6px' }}>
                              <TagsOutlined style={{ fontSize: '12px', color: token.colorPrimary }} />
                              <Text style={{
                                fontSize: '11px',
                                fontWeight: '500',
                                color: token.colorPrimary
                              }}>
                                Options ({parameter.enumValues.length})
                              </Text>
                            </Flex>
                            <Flex gap="4px" wrap>
                              {parameter.enumValues.slice(0, 4).map((value, index) => (
                                <span
                                  key={index}
                                  style={{
                                    fontSize: '10px',
                                    padding: '3px 6px',
                                    borderRadius: '6px',
                                    backgroundColor: token.colorBgContainer,
                                    border: `1px solid ${token.colorBorder}`,
                                    color: token.colorText,
                                    fontFamily: 'monospace',
                                    display: 'inline-block'
                                  }}
                                >
                                  {value.length > 12 ? `${value.slice(0, 12)}...` : value}
                                </span>
                              ))}
                              {parameter.enumValues.length > 4 && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    padding: '3px 6px',
                                    borderRadius: '6px',
                                    backgroundColor: token.colorPrimary,
                                    color: 'white',
                                    fontWeight: '500',
                                    display: 'inline-block'
                                  }}
                                >
                                  +{parameter.enumValues.length - 4}
                                </span>
                              )}
                            </Flex>
                          </div>
                        )}
                      </Flex>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        )}

        {/* Action Buttons */}
        <Flex justify="end" gap="medium">
          <Button onClick={onCancel} size="large">Cancel</Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!selectedWorkflow || !customName.trim() || !customUrl.trim()}
            size="large"
          >
            Add to Flow
          </Button>
        </Flex>
      </Flex>

      {/* Parameter Form Modal */}
      <Modal
        title={editingParameter ? 'Edit Parameter' : 'Add Parameter'}
        open={showParameterForm}
        onCancel={() => setShowParameterForm(false)}
        onOk={handleSaveParameter}
        okText={editingParameter ? 'Update' : 'Add'}
        width={500}
      >
        <Form form={form} layout="vertical" style={{ marginTop: token.marginMD }}>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="name"
                label="Name"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/, message: 'Use camelCase' }
                ]}
              >
                <Input placeholder="userId" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dataType" label="Type" rules={[{ required: true }]}>
                <Select placeholder="Select type">
                  {dataTypeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="required" valuePropName="checked" label=" " style={{ textAlign: 'center' }}>
                <Checkbox style={{ marginTop: '8px' }}>Required</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <TextArea rows={2} placeholder="What this parameter is for..." />
          </Form.Item>

          <Form.Item name="enumValues" label="Options (one per line)">
            <TextArea rows={3} placeholder="Option 1&#10;Option 2&#10;Option 3" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};